import { useEffect } from 'react';
import { authApi } from '../api/auth.api';
import { readUserSnapshot, useAuthStore } from '../stores/auth.store';

export function useBootAuth() {
  const { setAuth, setOfflineAuth, clearAuth, setLoading } = useAuthStore();

  useEffect(() => {
    // AuthCallbackPage owns the auth handshake on the OAuth return URL.
    // Running a parallel /auth/refresh here creates a race: if it fails first
    // we'd clearAuth() and kick the user to /login before the callback finishes.
    if (window.location.pathname === '/auth/callback') {
      setLoading(false);
      return;
    }
    authApi
      .refresh()
      .then(({ accessToken, user }) => setAuth(user, accessToken))
      .catch((error: { response?: unknown }) => {
        // Don't wipe out state another auth flow may have just set
        if (useAuthStore.getState().user) return;
        // A refresh with no response never reached the server, so it says
        // nothing about whether the session is still good. Signing out here is
        // what made the whole app unusable without a connection: every route is
        // behind ProtectedRoute, so a cached trip could never be reached.
        const snapshot = error?.response ? null : readUserSnapshot();
        if (snapshot) setOfflineAuth(snapshot);
        else clearAuth();
      })
      .finally(() => setLoading(false));
  }, []);

  // Coming back online promotes the offline session to a real one, so writes
  // start working again without a reload.
  useEffect(() => {
    function onOnline() {
      const auth = useAuthStore.getState();
      if (!auth.isOfflineSession) return;
      authApi
        .refresh()
        .then(({ accessToken, user }) => auth.setAuth(user, accessToken))
        .catch(() => null);
    }
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);
}
